import { useState } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { FirstAid, Plus, Trash, ShieldCheck, SealCheck, WarningCircle } from "@phosphor-icons/react";
import { isParent, isStaff, isAdmin, can } from "@/lib/roles";
import { SourceDocumentFields, useSourceDocuments } from "@/pages/admin/student/SourceDocuments";

const STATUSES = [["unconfirmed", "Not yet confirmed"], ["none_reported", "None reported"],
                  ["reported", "Allergy reported"]];
const DECISIONS = [["reported", "Allergy reported — details are on the record"],
                   ["none_reported", "None reported — the family signed a declaration"],
                   ["needs_documentation", "Needs documentation — evidence is still being obtained"]];
const TONE = { unconfirmed: "bg-amber-50 text-amber-800 border-amber-200",
               none_reported: "bg-emerald-50 text-emerald-700 border-emerald-200",
               reported: "bg-red-50 text-red-700 border-red-200" };
const ENTRY_FIELDS = [["trigger", "Trigger"], ["reaction", "Reaction"], ["avoidance", "Avoidance instructions"],
                      ["accommodations", "Classroom accommodations"], ["emergency_plan", "Emergency response plan"],
                      ["authorization", "Document / authorization"], ["review_date", "Review date"],
                      ["verified_by", "Verified by"]];
const blankEntry = { trigger: "", reaction: "", severity: "Unknown", avoidance: "", accommodations: "",
                     emergency_plan: "", document_name: "", authorization: "", review_date: "",
                     verified_by: "", verified_at: "", restricted_notes: "" };

// Allergy rows can be removed from the middle of the list, so each one keeps its own identity
// instead of relying on the array index.
let keySeq = 0;
const newEntry = () => { keySeq += 1; return { ...blankEntry, key: `a${keySeq}` }; };

export const AllergyPanel = ({ rec, student, user, onSave, onChanged }) => {
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState(null);
  const [request, setRequest] = useState(null);
  const [confirmForm, setConfirmForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const canEdit = can(user, "health", "edit") && !isParent(user);
  const canReview = canEdit && isAdmin(user);
  const status = rec.allergy_status || "unconfirmed";
  const entries = rec.allergy_entries || [];
  const severities = rec.severities || ["Unknown", "Mild", "Moderate", "Severe", "Life-threatening"];
  const review = rec.allergy_review || {};
  const needsReview = rec.allergy_needs_review !== false && !isParent(user);
  const { docs, reloadDocs } = useSourceDocuments(student.id, "allergy");
  const [docTypes, setDocTypes] = useState({});

  const startConfirm = async () => {
    try {
      const { data } = await api.get("/health/review-policy");
      setDocTypes(data.allergy_document_types || {});
    } catch { /* a non-director cannot read the policy; the dialog still works */ }
    setConfirmForm({ decision: status === "reported" ? "reported" : "", reviewer_name: user?.name || "",
                     next_review_date: "", document_id: "", note: "" });
  };

  const submitConfirm = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/students/${student.id}/allergy-review`, confirmForm);
      toast.success(data.decision === "needs_documentation"
        ? "Recorded as needing documentation — the status stays unconfirmed"
        : "Allergy status confirmed and written to the audit log");
      setConfirmForm(null);
      onChanged?.();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setSaving(false);
  };

  const start = () => {
    setForm({ allergy_status: status, allergy_entries: entries.map((e) => ({ ...newEntry(), ...e })),
              allergy_review_date: rec.allergy_review_date || "", allergy_verified_by: rec.allergy_verified_by || "",
              reason: "" });
    setEdit(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await onSave({ allergy_status: form.allergy_status,
                     allergy_entries: form.allergy_entries.map(({ key, ...rest }) => rest),
                     allergy_review_date: form.allergy_review_date, allergy_verified_by: form.allergy_verified_by },
                   form.reason);
      toast.success("Allergy information saved");
      setEdit(false);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setSaving(false);
  };

  const submitRequest = async () => {
    setSaving(true);
    try {
      await api.post(`/students/${student.id}/health/requests`, request);
      toast.success("Sent to staff for review — the approved record is unchanged until they apply it");
      setRequest(null);
      onChanged?.();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setSaving(false);
  };

  const acknowledge = async () => {
    try {
      await api.post(`/students/${student.id}/health/acknowledge`, { note: "" });
      toast.success("Acknowledgement recorded in the audit log");
      onChanged?.();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className={`rounded-2xl border p-5 sm:p-6 ${TONE[status]}`} data-testid="allergy-panel">
      <div className="flex flex-wrap gap-3 items-start justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-bold">Allergy status</p>
          <h2 className="mt-1 font-display text-2xl font-bold flex items-center gap-2" data-testid="allergy-status">
            <FirstAid size={22} /> {rec.allergy_status_label || "Not yet confirmed"}
          </h2>
          {status === "reported" && <p className="mt-1 font-bold" data-testid="allergy-summary">{rec.allergy_summary}</p>}
          {status === "unconfirmed" && (
            <p className="mt-1 text-sm font-semibold" data-testid="allergy-unconfirmed-note">
              A blank allergy field is not a confirmation. Confirm with the family and record the result.
            </p>
          )}
          {(rec.allergy_review_date || rec.allergy_verified_by) && (
            <p className="mt-1 text-xs font-semibold">
              {rec.allergy_verified_by ? `Verified by ${rec.allergy_verified_by}` : ""}
              {rec.allergy_review_date ? ` · review ${rec.allergy_review_date}` : ""}
            </p>
          )}
          {review.reviewed_at && (
            <p className="mt-1 text-xs font-semibold" data-testid="allergy-review-identity">
              Confirmed by {review.reviewed_by_email} on {(review.reviewed_at || "").slice(0, 10)}
              {review.document_version ? ` · document v${review.document_version}` : ""}
            </p>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {canReview && (
            <Button size="sm" onClick={startConfirm} className="rounded-full bg-[#10B981] hover:bg-emerald-600 font-bold"
                    data-testid="confirm-allergy-btn"><SealCheck size={15} className="mr-1" /> Confirm status</Button>
          )}
          {isStaff(user) && !isParent(user) && (
            <Button size="sm" variant="outline" onClick={acknowledge} className="rounded-full font-bold bg-white"
                    data-testid="acknowledge-health-btn"><ShieldCheck size={15} className="mr-1" /> I have read this</Button>
          )}
          {canEdit && !edit && (
            <Button size="sm" onClick={start} className="rounded-full bg-[#0F172A] hover:bg-slate-800 font-bold"
                    data-testid="edit-allergy-btn">Update allergy record</Button>
          )}
          {isParent(user) && (
            <Button size="sm" onClick={() => setRequest({ area: "allergies", details: "", requested_change: "", effective_date: "" })}
                    className="rounded-full bg-[#3B82F6] hover:bg-[#2563EB] font-bold" data-testid="request-health-change-btn">
              Submit a correction
            </Button>
          )}
        </div>
      </div>

      {needsReview && (
        <div className="mt-4 rounded-xl bg-white/80 border border-amber-300 p-4" data-testid="allergy-review-needed">
          <p className="text-sm font-bold flex items-center gap-2">
            <WarningCircle size={16} /> A director has not confirmed this allergy status yet.
          </p>
          {review.decision === "needs_documentation" && (
            <p className="mt-1 text-sm font-semibold" data-testid="allergy-needs-documentation">
              Marked as needing documentation{review.note ? ` — ${review.note}` : ""}.
            </p>
          )}
          {rec.legacy_allergy_note && (
            <p className="mt-1 text-sm" data-testid="allergy-legacy-note">{rec.legacy_allergy_note}</p>
          )}
          {rec.dietary_restrictions && (
            <p className="mt-1 text-sm" data-testid="allergy-dietary-note">
              A dietary restriction is on file: &ldquo;{rec.dietary_restrictions}&rdquo;. A dietary restriction is
              not an allergy status — confirm the allergy separately and leave the dietary note unchanged.
            </p>
          )}
        </div>
      )}

      {rec.allergy_care_plan_pending && (
        <p className="mt-3 text-sm font-bold rounded-xl bg-white/80 border border-red-300 p-4"
           data-testid="allergy-care-plan-pending">
          Allergy confirmed, but the written care plan is still being obtained. The classroom warning stays visible.
        </p>
      )}

      {entries.length > 0 && (
        <div className="mt-4 grid gap-3 lg:grid-cols-2" data-testid="allergy-entries">
          {entries.map((e, i) => (
            <div key={`${e.trigger}-${i}`} className="rounded-xl bg-white/80 border border-border p-4"
                 data-testid={`allergy-entry-${i}`}>
              <p className="font-bold">{e.trigger || "Allergy"}{e.severity && e.severity !== "Unknown" ? ` · ${e.severity}` : ""}</p>
              <dl className="mt-2 space-y-1 text-sm">
                {[["Reaction", e.reaction], ["Avoid", e.avoidance], ["Classroom plan", e.accommodations],
                  ["If exposed", e.emergency_plan], ["Document", e.document_name || e.authorization],
                  ["Review date", e.review_date], ["Verified by", e.verified_by]]
                  .filter(([, v]) => v).map(([k, v]) => (
                  <div key={k}><dt className="text-xs uppercase tracking-wider font-bold text-muted-foreground">{k}</dt>
                    <dd className="font-semibold whitespace-pre-wrap">{v}</dd></div>
                ))}
              </dl>
            </div>
          ))}
        </div>
      )}

      {Array.isArray(rec.my_requests) && rec.my_requests.length > 0 && (
        <div className="mt-4 rounded-xl bg-white/80 border border-border p-4" data-testid="my-health-requests">
          <p className="text-xs uppercase tracking-wider font-bold text-muted-foreground">My submitted corrections</p>
          <ul className="mt-2 space-y-1 text-sm">
            {rec.my_requests.map((r) => (
              <li key={r.id} className="font-semibold" data-testid={`my-request-${r.id}`}>
                {(r.created_at || "").slice(0, 10)} · {r.area_label} · <span className="capitalize">{r.status}</span>
                {r.review_note ? ` — ${r.review_note}` : ""}
              </li>
            ))}
          </ul>
        </div>
      )}

      <Dialog open={edit} onOpenChange={(o) => !o && setEdit(false)}>
        <DialogContent className="rounded-3xl max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="allergy-dialog">
          <DialogHeader><DialogTitle className="font-display">Allergy record — {student.name}</DialogTitle>
            <DialogDescription>Every change is written to the health access log with your name and reason.</DialogDescription></DialogHeader>
          {form && (
            <div className="space-y-4">
              <div><Label className="text-sm font-bold">Status</Label>
                <Select value={form.allergy_status} onValueChange={(v) => setForm({ ...form, allergy_status: v,
                        allergy_entries: v === "reported" && form.allergy_entries.length === 0 ? [newEntry()] : form.allergy_entries })}>
                  <SelectTrigger className="mt-1 h-11 rounded-xl" data-testid="allergy-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                </Select></div>
              {form.allergy_status === "reported" && (
                <>
                  {form.allergy_entries.map((e, i) => (
                    <div key={e.key} className="rounded-2xl border border-border p-4" data-testid={`allergy-edit-entry-${i}`}>
                      <div className="flex justify-between items-center gap-2">
                        <p className="font-bold text-sm">Allergy {i + 1}</p>
                        <Button size="sm" variant="ghost" className="rounded-full text-[#EF4444]"
                                onClick={() => setForm({ ...form, allergy_entries: form.allergy_entries.filter((_, j) => j !== i) })}
                                data-testid={`remove-allergy-${i}`}><Trash size={15} /></Button>
                      </div>
                      <div className="mt-2 grid sm:grid-cols-2 gap-3">
                        <div><Label className="text-sm font-bold">Severity</Label>
                          <Select value={e.severity || "Unknown"} onValueChange={(v) => {
                            const next = [...form.allergy_entries]; next[i] = { ...e, severity: v };
                            setForm({ ...form, allergy_entries: next });
                          }}>
                            <SelectTrigger className="mt-1 h-11 rounded-xl" data-testid={`allergy-severity-${i}`}><SelectValue /></SelectTrigger>
                            <SelectContent>{severities.map((sv) => <SelectItem key={sv} value={sv}>{sv}</SelectItem>)}</SelectContent>
                          </Select></div>
                        {ENTRY_FIELDS.map(([k, label]) => (
                          <div key={k} className={["avoidance", "accommodations", "emergency_plan"].includes(k) ? "sm:col-span-2" : ""}>
                            <Label className="text-sm font-bold">{label}</Label>
                            {["avoidance", "accommodations", "emergency_plan"].includes(k) ? (
                              <Textarea value={e[k] || ""} onChange={(ev) => {
                                const next = [...form.allergy_entries]; next[i] = { ...e, [k]: ev.target.value };
                                setForm({ ...form, allergy_entries: next });
                              }} className="mt-1 rounded-xl" data-testid={`allergy-${k}-${i}`} />
                            ) : (
                              <Input type={k === "review_date" ? "date" : "text"} value={e[k] || ""} onChange={(ev) => {
                                const next = [...form.allergy_entries]; next[i] = { ...e, [k]: ev.target.value };
                                setForm({ ...form, allergy_entries: next });
                              }} className="mt-1 h-11 rounded-xl" data-testid={`allergy-${k}-${i}`} />
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                  <Button variant="outline" className="rounded-full font-bold"
                          onClick={() => setForm({ ...form, allergy_entries: [...form.allergy_entries, newEntry()] })}
                          data-testid="add-allergy-btn"><Plus size={15} className="mr-1" /> Add another allergy</Button>
                </>
              )}
              <div className="grid sm:grid-cols-2 gap-3">
                <div><Label className="text-sm font-bold">Record review date</Label>
                  <Input type="date" value={form.allergy_review_date} onChange={(e) => setForm({ ...form, allergy_review_date: e.target.value })}
                         className="mt-1 h-11 rounded-xl" data-testid="allergy-review-date" /></div>
                <div><Label className="text-sm font-bold">Who verified this information</Label>
                  <Input value={form.allergy_verified_by} onChange={(e) => setForm({ ...form, allergy_verified_by: e.target.value })}
                         className="mt-1 h-11 rounded-xl" data-testid="allergy-verified-by" /></div>
              </div>
              <div><Label className="text-sm font-bold">Reason for change</Label>
                <Input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })}
                       className="mt-1 h-11 rounded-xl" data-testid="allergy-reason" /></div>
              <Button onClick={save} disabled={saving} className="w-full h-11 rounded-full bg-[#3B82F6] hover:bg-[#2563EB] font-bold"
                      data-testid="save-allergy-btn">{saving ? "Saving…" : "Save allergy record"}</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(confirmForm)} onOpenChange={(o) => !o && setConfirmForm(null)}>
        <DialogContent className="rounded-3xl max-w-xl max-h-[90vh] overflow-y-auto" data-testid="allergy-confirm-dialog">
          <DialogHeader><DialogTitle className="font-display">Confirm allergy status — {student.name}</DialogTitle>
            <DialogDescription>
              Your account and the time are recorded automatically. A status is never confirmed without a linked
              source document, and nothing is ever set to &ldquo;None reported&rdquo; on your behalf.
            </DialogDescription></DialogHeader>
          {confirmForm && (
            <div className="space-y-4">
              {rec.dietary_restrictions && (
                <p className="text-sm font-semibold rounded-2xl bg-amber-50 border border-amber-200 p-3"
                   data-testid="confirm-dietary-warning">
                  On file: &ldquo;{rec.dietary_restrictions}&rdquo;. Review it with the family, but it does not by
                  itself determine the allergy status and will not be changed here.
                </p>
              )}
              <div><Label className="text-sm font-bold">Decision</Label>
                <Select value={confirmForm.decision} onValueChange={(v) => setConfirmForm({ ...confirmForm, decision: v })}>
                  <SelectTrigger className="mt-1 h-11 rounded-xl" data-testid="confirm-decision"><SelectValue placeholder="Choose the reviewed outcome" /></SelectTrigger>
                  <SelectContent>{DECISIONS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                </Select></div>
              {confirmForm.decision !== "needs_documentation" && (
                <SourceDocumentFields studentId={student.id} context="allergy" types={docTypes} docs={docs}
                                      value={confirmForm.document_id} testPrefix="allergy"
                                      onChange={(id) => setConfirmForm({ ...confirmForm, document_id: id })}
                                      onFiled={reloadDocs} />
              )}
              <div className="grid sm:grid-cols-2 gap-3">
                <div><Label className="text-sm font-bold">Reviewer name (for display)</Label>
                  <Input value={confirmForm.reviewer_name} onChange={(e) => setConfirmForm({ ...confirmForm, reviewer_name: e.target.value })}
                         className="mt-1 h-11 rounded-xl" data-testid="confirm-reviewer-name" />
                  <p className="mt-1 text-xs text-muted-foreground">Signed in as {user?.email} — that is the record of identity.</p></div>
                <div><Label className="text-sm font-bold">Next review date</Label>
                  <Input type="date" value={confirmForm.next_review_date} onChange={(e) => setConfirmForm({ ...confirmForm, next_review_date: e.target.value })}
                         className="mt-1 h-11 rounded-xl" data-testid="confirm-next-review" /></div>
              </div>
              <div><Label className="text-sm font-bold">Note to file</Label>
                <Textarea value={confirmForm.note} onChange={(e) => setConfirmForm({ ...confirmForm, note: e.target.value })}
                          className="mt-1 rounded-xl" data-testid="confirm-note" /></div>
              <Button onClick={submitConfirm} disabled={saving || !confirmForm.decision}
                      className="w-full h-11 rounded-full bg-[#10B981] hover:bg-emerald-600 font-bold"
                      data-testid="confirm-allergy-save">{saving ? "Recording…" : "Record this review"}</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(request)} onOpenChange={(o) => !o && setRequest(null)}>
        <DialogContent className="rounded-3xl max-w-lg" data-testid="health-request-dialog">
          <DialogHeader><DialogTitle className="font-display">Submit a health correction</DialogTitle>
            <DialogDescription>Staff review every submission. The approved care plan does not change until they apply it.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-sm font-bold">What is this about</Label>
              <Select value={request?.area} onValueChange={(v) => setRequest({ ...request, area: v })}>
                <SelectTrigger className="mt-1 h-11 rounded-xl" data-testid="request-area"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(rec.request_areas || { allergies: "Allergies", other: "Something else" })
                  .map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label className="text-sm font-bold">What should change</Label>
              <Textarea value={request?.details || ""} onChange={(e) => setRequest({ ...request, details: e.target.value })}
                        className="mt-1 rounded-xl" data-testid="request-details" /></div>
            <div><Label className="text-sm font-bold">Exact wording you would like on file (optional)</Label>
              <Textarea value={request?.requested_change || ""} onChange={(e) => setRequest({ ...request, requested_change: e.target.value })}
                        className="mt-1 rounded-xl" data-testid="request-change" /></div>
            <Button onClick={submitRequest} disabled={saving || (request?.details || "").trim().length < 3}
                    className="w-full h-11 rounded-full bg-[#3B82F6] hover:bg-[#2563EB] font-bold"
                    data-testid="submit-health-request">{saving ? "Sending…" : "Send to staff"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
